<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";

const props = defineProps<{
  visible: boolean;
  progress: {
    current: number;
    total: number;
  };
  error?: string | null;
}>();

const emit = defineEmits<{
  (e: "close"): void;
}>();

const { t } = useTranslation();

const progressPercent = computed(() => {
  if (props.progress.total === 0) return 0;
  return Math.round((props.progress.current / props.progress.total) * 100);
});

const progressText = computed(() => {
  return `${props.progress.current} / ${props.progress.total}`;
});
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="visible" class="iqoa-overlay">
        <div class="overlay-content">
          <!-- AI Icon with pulse animation -->
          <div class="ai-icon-container">
            <div class="pulse-ring"></div>
            <div class="pulse-ring delay-1"></div>
            <div class="ai-icon">
              <i class="bi-book" />
            </div>
          </div>

          <!-- Title -->
          <h2 class="overlay-title">
            {{ t("report:ai.iqoa.overlay.title") }}
          </h2>

          <!-- Progress bar -->
          <div class="progress-container">
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: `${progressPercent}%` }"
              />
            </div>
            <div class="progress-info">
              <span class="progress-text">{{ progressText }}</span>
              <span class="progress-label">
                {{ t("report:ai.iqoa.overlay.findings") }}
              </span>
            </div>
          </div>

          <!-- Steps description -->
          <div class="steps-description">
            <div class="step-item">
              <div class="step-indicator">
                <div v-if="progressPercent < 100" class="spinner" />
                <i v-else class="bi-check-lg" />
              </div>
              <span class="step-label">
                {{ t("report:ai.iqoa.overlay.searching") }}
              </span>
            </div>
          </div>

          <!-- Message -->
          <p v-if="error" class="overlay-message error-message">
            {{ error }}
          </p>
          <p v-else class="overlay-message">
            {{ t("common:pleaseWait") }}
          </p>

          <!-- Close button -->
          <button class="close-btn" @click="emit('close')">
            {{ t("report:ai.overlay.minimize") }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.iqoa-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 32px;
  max-width: 400px;
  width: 100%;
}

.ai-icon-container {
  position: relative;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid rgba(139, 92, 246, 0.5);
  animation: pulse 1.5s ease-out infinite;
}

.pulse-ring.delay-1 {
  animation-delay: 0.75s;
}

@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

.ai-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 36px;
  z-index: 1;
  animation: iconPulse 2s ease-in-out infinite;
}

@keyframes iconPulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.overlay-title {
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  text-align: center;
}

.progress-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #8b5cf6 0%, #a78bfa 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-text {
  color: white;
  font-size: 16px;
  font-weight: 600;
}

.progress-label {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
}

.steps-description {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background-color: rgba(139, 92, 246, 0.2);
}

.step-indicator {
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
  color: #22c55e;
  font-size: 14px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #8b5cf6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.step-label {
  color: #a78bfa;
  font-size: 14px;
  font-weight: 500;
}

.overlay-message {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  margin: 0;
  text-align: center;

  &.error-message {
    color: #ef4444;
  }
}

.close-btn {
  margin-top: 8px;
  padding: 8px 20px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    color: white;
  }
}

// Transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
